import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import PrismLogo from "@/components/PrismLogo";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [demoAccounts, setDemoAccounts] = useState([]);

  useEffect(() => {
    // Demo credentials come from the backend so they never live in the frontend bundle.
    // Backend returns [] in production (SHOW_DEMO_LOGINS=false) which hides the section.
    api.get("/auth/demo-accounts")
      .then((r) => setDemoAccounts(r.data || []))
      .catch(() => setDemoAccounts([]));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const res = await login(email.trim(), password);
    setBusy(false);
    if (!res.ok) {
      setErr(res.error);
      return;
    }
    const target =
      res.user.role === "admin" ? "/admin" : res.user.role === "teacher" ? "/teacher" : "/dashboard";
    nav(target);
  };

  const fillDemo = (d) => {
    setEmail(d.email);
    setPassword(d.password);
  };

  return (
    <div className="min-h-screen bg-slate-50 grid lg:grid-cols-2" data-testid="login-page">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-indigo-600 via-indigo-700 to-violet-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 qs-grid-bg" />
        <Link to="/" className="flex items-center gap-2 relative">
          <PrismLogo size={40} />
          <span className="font-display text-2xl font-extrabold">Prismiq</span>
        </Link>
        <div className="relative">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-indigo-200">Welcome back</p>
          <h1 className="font-display text-4xl xl:text-5xl font-black mt-4 leading-tight">
            Log in to run,<br />take or manage exams.
          </h1>
          <p className="mt-4 text-indigo-100 max-w-md">
            Try Prismiq instantly with one of the demo accounts on the right — no setup required.
          </p>
        </div>
        <p className="relative text-xs text-indigo-200">© {new Date().getFullYear()} Prismiq</p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <PrismLogo size={36} />
            <span className="font-display text-xl font-extrabold text-slate-900">Prismiq</span>
          </div>

          <h2 className="font-display text-3xl font-black text-slate-900">Sign in</h2>
          <p className="mt-1.5 text-slate-500 text-sm">Access your dashboard, exams and analytics.</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition"
                placeholder="you@school.edu"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
            {err && (
              <div className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2" data-testid="login-error">
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full justify-center disabled:opacity-70"
              data-testid="login-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {busy ? "Signing in…" : "Sign in"}
            </button>
            <div className="text-right">
              <Link to="/forgot-password" className="text-xs font-semibold text-indigo-600 hover:underline" data-testid="link-forgot">
                Forgot password?
              </Link>
            </div>
          </form>

          <p className="mt-6 text-sm text-slate-500">
            No account?{" "}
            <Link to="/register" className="text-indigo-600 font-semibold hover:underline" data-testid="link-register">
              Create one
            </Link>
          </p>

          <div className="mt-8">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">Try a demo account</p>
            {demoAccounts.length === 0 ? (
              <p className="text-xs text-slate-400 italic">Demo accounts are disabled in this environment.</p>
            ) : (
              <div className="grid gap-2">
                {demoAccounts.map((d) => (
                  <button
                    key={d.role}
                    type="button"
                    onClick={() => fillDemo(d)}
                    className={`text-left px-4 py-3 rounded-xl border ${d.tone} hover:brightness-95 transition`}
                    data-testid={`demo-${d.role.toLowerCase()}-button`}
                  >
                    <p className="font-semibold text-sm">{d.role}</p>
                    <p className="text-xs opacity-80">{d.email} · {d.password}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
